import React from 'react'
import {Box, Flex, Stack, Text} from '../../vanilla'
import {Hit} from '../../store/ProductStore'
import {Modal} from '../modal'
import {observer} from 'mobx-react-lite'
import {ProductImage} from '../product-detail/product-image'
import {useProductDetail} from '../../store/hooks/useProductDetail'
import {
  highlightIngredientsWithAllergens,
  allergensProductMayContain,
  formatAllergens,
} from '../../utils/allergen-ingredients-utils'
import {capitalize} from '../../utils/utils'
import {NutritionalTable} from '../product-detail/product-info/nutritional-modal'

type MoreInfoProps = {
  hit: Hit
  isOpen: boolean
  onClose: () => void
}

const MoreInfoContainer = ({hit, isOpen, onClose}: MoreInfoProps) => {
  // @todo format the algolia data to be consistent across PLP and PDP - happens in price component too
  // @ts-ignore
  const img = hit.image_groups?.[0]?.images?.[0] || hit.imageGroups?.[0]?.images?.[0]
  const imgAlt = hit.image_groups?.[0]?.images?.[0]?.alt || hit.name || 'Product Image'
  const productDetail = useProductDetail(hit.id)
  const {product} = productDetail
  const {allergenMayContain, nutritionInformation, ingredients, allergenInfo, allergenContains} =
    product || {}
  const allergens = allergenContains || []
  const allergensProductMayContainList = allergensProductMayContain(allergenMayContain) || []
  const showIngredients = ingredients || allergenContains || allergenMayContain || allergenInfo
  const showNutritionalTable =
    nutritionInformation &&
    Array.isArray(nutritionInformation?.nutrients) &&
    nutritionInformation.nutrients.length > 0
  const globalAllergenWords = [
    'milk',
    'eggs',
    'fish',
    'shellfish',
    'tree nuts',
    'peanuts',
    'wheat',
    'soy',
  ]

  const ModalHeaderContent = (
    <Flex direction="row" gap="8px" alignContent={'flex-start'} alignItems="stretch" maxHeight="122px">
      <ProductImage
        image={img}
        imageAlt={imgAlt}
        isThumbnail={false}
        inDrawer={false}
        maxHeight="96px"
        alignSelf="center"
      />
      <Text alignSelf="center" lineHeight="short" variant="text4">
        {hit.name}
      </Text>
    </Flex>
  )

  return (
    <Modal title={ModalHeaderContent} onClose={onClose} isOpen={isOpen} size="full-lg-m-auto" stickyHeader={true} allowScroll={true}>
      <Flex direction="column" gap="16px" bg="white">
        {showIngredients && ingredients && (
          <Stack padding="16px" data-test-selector="ingredients-list">
            <Text variant="text3Bold" color="gray1000">
              Ingredients
            </Text>
            {ingredients.map((ingredientsText: string, index: number) => (
              <Text
                key={index}
                variant="text2"
                color="gray1000"
                lineHeight="short"
                dangerouslySetInnerHTML={{
                  __html: highlightIngredientsWithAllergens(ingredientsText, [
                    ...allergens,
                    ...globalAllergenWords,
                  ]),
                }}
              />
            ))}
          </Stack>
        )}
        {allergenInfo && (
          <Stack spacing="8px" padding="16px" data-test-selector="allergen-info">
            <Text variant="text2" color="gray1000" lineHeight="short">
              {allergenInfo}
            </Text>
          </Stack>
        )}
        {allergenContains && (
          <Stack spacing="8px" padding="16px" data-test-selector="allergen-contains">
            <Text variant="text3Bold" color="gray1000">
              Allergy Information
            </Text>
            <Text variant="text2" color="gray1000" lineHeight="short">
              Contains:{' '}
              <span style={{fontWeight: 'bold'}}>
                {ingredients
                  ? formatAllergens(ingredients.join(', '), allergenContains, globalAllergenWords)
                  : allergenContains.map((allergen: string) => capitalize(allergen)).join(', ')}
              </span>
            </Text>
            {allergensProductMayContainList.length != 0 && (
              <Text variant="text2" color="gray1000" lineHeight="short">
                May contain:{' '}
                <span style={{fontWeight: 'bold'}}>{allergensProductMayContainList}</span>
              </Text>
            )}
          </Stack>
        )}
        {showNutritionalTable && (
          <Stack spacing="8px" padding="16px" data-test-selector="nutritional-info" width="full">
            <Text variant="text3Bold" color="gray1000">
              Nutritional Info
            </Text>
            <Box overflowX="scroll">
            <NutritionalTable data={nutritionInformation} styleChanges={{headerTextVariant: 'text2', headerTextWeight: 'bold', tableTextVariant: 'text2', tableRowStyling: {backgroundColor: 'white', borderTop:'2px solid #e0e0e0'}}}/>
            </Box>
          </Stack>
        )}
      </Flex>
    </Modal>
  )
}

export const MoreInfo = observer(MoreInfoContainer)